import type {
    FlowTrade,
    OptionsProfileStrike,
} from "../components/charts/gex/types";

export type FlowDirection = "bullish" | "bearish" | "neutral";

export interface FlowProfileLevel {
    strike: number;
    chartPrice: number;
    callNet: number;
    putNet: number;
    netPremium: number;
    totalVolume: number;
    direction: FlowDirection;
    directionScore: number;
    isPoc: boolean;
    isKeyLevel: boolean;
    rank: number;
}

export interface FlowWhalePrint {
    timestamp: number;
    strike: number;
    chartPrice: number;
    premium: number;
    volume: number;
    side: "CALL" | "PUT";
    position: FlowTrade["position"];
    direction: FlowDirection;
}

/** Single flow print on the chart at (time × strike). */
export interface FlowOpportunityMarker {
    timestamp: number;
    strike: number;
    chartPrice: number;
    premium: number;
    volume: number;
    side: "CALL" | "PUT";
    position: FlowTrade["position"];
    direction: FlowDirection;
    /** Short tag: BC, SC, LP, SP */
    label: string;
    isWhale: boolean;
}

const WHALE_PREMIUM = 50_000;
const FLOW_OP_MIN_PREMIUM = 2_500;
const FLOW_OP_MAX_MARKERS = 800;
const KEY_LEVEL_COUNT = 5;

/** Infer long/short from tape side, NBBO, or print vs mid (handles Side=Mid from QuestDB). */
export function inferFlowTradePosition(row: {
    position?: FlowTrade["position"];
    trade_side?: string;
    Trade_Side?: string;
    Bid_Ask?: string;
    bid_ask?: string;
    Side?: string;
    side?: string;
    Fill_vs_Spread?: string;
    fill_vs_spread?: string;
    Aggressor?: string;
    aggressor?: string;
    vs_mid?: number;
    Vs_Mid?: number;
    Last?: number;
    price?: number;
    Price?: number;
    bid?: number;
    Bid?: number;
    ask?: number;
    Ask?: number;
    nbbo_bid?: number;
    nbbo_ask?: number;
    nbbo_mid?: number;
}): FlowTrade["position"] {
    if (row.position === "LONG" || row.position === "SHORT") {
        return row.position;
    }

    const raw = String(
        row.trade_side ||
            row.Trade_Side ||
            row.Bid_Ask ||
            row.bid_ask ||
            row.Fill_vs_Spread ||
            row.fill_vs_spread ||
            row.Side ||
            row.side ||
            "",
    )
        .trim()
        .toLowerCase();

    if (
        raw === "ask" ||
        raw === "buy" ||
        raw === "b" ||
        raw.includes("at ask") ||
        raw.includes("lift") ||
        raw.includes("above ask")
    ) {
        return "LONG";
    }
    if (
        raw === "bid" ||
        raw === "sell" ||
        raw === "s" ||
        raw.includes("at bid") ||
        raw.includes("hit") ||
        raw.includes("below bid")
    ) {
        return "SHORT";
    }

    const vm = Number(row.vs_mid ?? row.Vs_Mid);
    if (Number.isFinite(vm) && vm !== 0) {
        if (vm > 0) return "LONG";
        if (vm < 0) return "SHORT";
    }

    const last = Number(
        row.Last ?? row.price ?? row.Price ?? 0,
    );
    const bid = Number(row.bid ?? row.Bid ?? row.nbbo_bid ?? 0);
    const ask = Number(row.ask ?? row.Ask ?? row.nbbo_ask ?? 0);
    const mid = Number(row.nbbo_mid ?? 0);
    if (last > 0 && ask > 0 && last >= ask * 0.998) return "LONG";
    if (last > 0 && bid > 0 && last <= bid * 1.002) return "SHORT";
    if (last > 0 && mid > 0) {
        if (last > mid * 1.001) return "LONG";
        if (last < mid * 0.999) return "SHORT";
    }

    const agg = String(row.Aggressor ?? row.aggressor ?? "")
        .trim()
        .toLowerCase();
    if (agg.includes("buy") || agg === "ask") return "LONG";
    if (agg.includes("sell") || agg === "bid") return "SHORT";

    return "UNKNOWN";
}

/**
 * Bullish: buy call (long call) or sell put (short put).
 * Bearish: sell call (short call) or buy put (long put).
 */
export function classifyFlowTradeDirection(t: FlowTrade): FlowDirection {
    const position = inferFlowTradePosition(t);
    if (position === "LONG" && t.side === "CALL") return "bullish";
    if (position === "SHORT" && t.side === "PUT") return "bullish";
    if (position === "SHORT" && t.side === "CALL") return "bearish";
    if (position === "LONG" && t.side === "PUT") return "bearish";
    return "neutral";
}

export function flowOpportunityLabel(t: FlowTrade): string {
    const p =
        t.position === "LONG" ? "B" : t.position === "SHORT" ? "S" : "?";
    const s = t.side === "CALL" ? "C" : "P";
    return `${p}${s}`;
}

export function buildFlowOpportunityMarkers(
    trades: FlowTrade[],
    opts?: {
        windowMs?: number;
        minPremium?: number;
        maxMarkers?: number;
        anchor?: number;
        nearAnchorPct?: number;
        chartScale?: number;
    },
): FlowOpportunityMarker[] {
    const windowMs = opts?.windowMs ?? 24 * 60 * 60 * 1000;
    const minPremium = opts?.minPremium ?? FLOW_OP_MIN_PREMIUM;
    const maxMarkers = opts?.maxMarkers ?? FLOW_OP_MAX_MARKERS;
    const anchor = opts?.anchor ?? 0;
    const nearPct = opts?.nearAnchorPct ?? 0.35;
    const chartScale =
        opts?.chartScale && opts.chartScale > 0 ? opts.chartScale : 1;
    const cutoff = Date.now() - windowMs;
    const band = anchor > 0 ? anchor * nearPct : 0;

    const out: FlowOpportunityMarker[] = [];
    for (const t of trades) {
        if (t.timestamp < cutoff || t.premium < minPremium || t.strike <= 0) {
            continue;
        }
        if (band > 0 && Math.abs(t.strike - anchor) > band) continue;
        const direction = classifyFlowTradeDirection(t);
        if (direction === "neutral") continue;
        out.push({
            timestamp: t.timestamp,
            strike: t.strike,
            chartPrice: t.strike * chartScale,
            premium: t.premium,
            volume: t.volume,
            side: t.side,
            position: t.position,
            direction,
            label: flowOpportunityLabel(t),
            isWhale: t.premium >= WHALE_PREMIUM,
        });
    }
    out.sort((a, b) => b.premium - a.premium);
    return out.slice(0, maxMarkers);
}

export function flowDirectionFromNets(
    callNet: number,
    putNet: number,
): { direction: FlowDirection; score: number } {
    const score = callNet - putNet;
    if (score > 0) return { direction: "bullish", score };
    if (score < 0) return { direction: "bearish", score };
    return { direction: "neutral", score: 0 };
}

/**
 * Build strike-based volume profile from flow-buffer trades (options profile aggregation).
 * Strikes are used as chart price levels (standard for index/equity GEX charts).
 */
export function buildFlowVolumeProfile(
    profile: OptionsProfileStrike[],
    trades: FlowTrade[],
    anchor: number,
    nearPct = 0.12,
): FlowProfileLevel[] {
    if (!profile.length || !anchor || anchor <= 0) return [];

    const band = anchor * nearPct;
    const premiumByStrike = new Map<number, number>();
    const volumeByStrike = new Map<number, number>();

    for (const t of trades) {
        if (!t.strike || t.strike <= 0) continue;
        if (Math.abs(t.strike - anchor) > band * 1.25) continue;
        premiumByStrike.set(
            t.strike,
            (premiumByStrike.get(t.strike) || 0) + Math.abs(t.premium),
        );
        volumeByStrike.set(
            t.strike,
            (volumeByStrike.get(t.strike) || 0) + Math.abs(t.volume),
        );
    }

    const near = profile.filter((p) => Math.abs(p.strike - anchor) <= band);
    if (!near.length) return [];

    let pocStrike = near[0].strike;
    let pocWeight = 0;
    for (const p of near) {
        const w =
            (premiumByStrike.get(p.strike) || 0) +
            Math.abs(p.callNet) +
            Math.abs(p.putNet);
        if (w > pocWeight) {
            pocWeight = w;
            pocStrike = p.strike;
        }
    }

    const ranked = [...near]
        .map((p) => ({
            p,
            weight:
                (premiumByStrike.get(p.strike) || 0) +
                Math.abs(p.net) * 100,
        }))
        .sort((a, b) => b.weight - a.weight);

    const keySet = new Set(
        ranked.slice(0, KEY_LEVEL_COUNT).map((r) => r.p.strike),
    );

    return near
        .map((p) => {
            const { direction, score } = flowDirectionFromNets(
                p.callNet,
                p.putNet,
            );
            const rankIdx = ranked.findIndex((r) => r.p.strike === p.strike);
            return {
                strike: p.strike,
                chartPrice: p.strike,
                callNet: p.callNet,
                putNet: p.putNet,
                netPremium: premiumByStrike.get(p.strike) || 0,
                totalVolume:
                    volumeByStrike.get(p.strike) ||
                    Math.abs(p.callNet) + Math.abs(p.putNet),
                direction,
                directionScore: score,
                isPoc: p.strike === pocStrike,
                isKeyLevel: keySet.has(p.strike),
                rank: rankIdx >= 0 ? rankIdx : 99,
            };
        })
        .sort((a, b) => b.strike - a.strike);
}

export function buildFlowWhalePrints(
    trades: FlowTrade[],
    anchor: number,
    windowMs = 24 * 60 * 60 * 1000,
    minPremium = WHALE_PREMIUM,
): FlowWhalePrint[] {
    if (!trades.length || !anchor || anchor <= 0) return [];
    const cutoff = Date.now() - windowMs;
    const band = anchor * 0.15;

    return trades
        .filter(
            (t) =>
                t.timestamp >= cutoff &&
                t.premium >= minPremium &&
                t.strike > 0 &&
                Math.abs(t.strike - anchor) <= band,
        )
        .sort((a, b) => b.premium - a.premium)
        .slice(0, 40)
        .map((t) => {
            const callNet =
                t.side === "CALL"
                    ? t.position === "LONG"
                        ? t.volume
                        : t.position === "SHORT"
                          ? -t.volume
                          : 0
                    : 0;
            const putNet =
                t.side === "PUT"
                    ? t.position === "LONG"
                        ? t.volume
                        : t.position === "SHORT"
                          ? -t.volume
                          : 0
                    : 0;
            const direction = classifyFlowTradeDirection(t);

            return {
                timestamp: t.timestamp,
                strike: t.strike,
                chartPrice: t.strike,
                premium: t.premium,
                volume: t.volume,
                side: t.side,
                position: t.position,
                direction,
            };
        });
}

export function filterFlowTradesForSymbol(
    trades: FlowTrade[],
    ...symbols: string[]
): FlowTrade[] {
    const set = new Set(
        symbols
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean),
    );
    if (!set.size) return trades;
    return trades.filter((t) => {
        const sym = String(t.symbol || "").toUpperCase();
        return set.has(sym);
    });
}
